import { useDispatch } from 'react-redux';
import { useMutation } from '@tanstack/react-query';
import ObrasApi from 'Data/Obras.api';
import { FILES_QUERY_KEY, QUERY_KEY_MOVE_FILES_LIST } from 'Data/keys/QueryKeys';
import { IUseQueryFilesReturn } from 'Data/interfaces/ListagemArquivos/IListFiles';
import { IUpdateFileInfoRequest } from 'Data/interfaces/Obra/IUpdateFileInfoRequest';
import { FederatedViewerActions } from 'Store/FederatedViewer/FederatedViewer.actions';
import { UploadActions } from 'Store/Upload/Upload.actions';
import { queryClient } from 'Context/QueryClientContext';
import toastHandler from 'Utils/toastHandler';

export const useQueryUpdateInfoFiles = () => {
  const dispatch = useDispatch();

  const { mutate, isPending } = useMutation({
    mutationFn: (variables: IUpdateFileInfoRequest) => {
      return ObrasApi.updateFileInfo(variables.files);
    },
    onSuccess: async ({ data }, variables) => {
      await Promise.all([
        await queryClient.cancelQueries({
          queryKey: [
            FILES_QUERY_KEY,
            Number(variables.constructionSiteId),
            variables.folderId,
          ],
        }),
        await queryClient.cancelQueries({
          queryKey: [
            QUERY_KEY_MOVE_FILES_LIST,
            Number(variables.constructionSiteId),
            variables.folderId,
          ],
        }),
      ]);

      queryClient.setQueryData<IUseQueryFilesReturn>(
        [FILES_QUERY_KEY, Number(variables.constructionSiteId), variables.folderId],
        (old) => {
          if (old) {
            return {
              ...old,
              ListFiles: old.ListFiles.map((file) => {
                const index = data.data.findIndex((info) => {
                  return info.ApiId === file.Identifier.ApiId;
                });
                if (index !== -1) {
                  return {
                    ...file,
                    FileInfo: {
                      ...file.FileInfo,
                      ...data.data[index],
                      Description: data.data[index].Description,
                      finished: true,
                    },
                    Status: data.data[index].Status,
                  };
                }
                return file;
              }),
            };
          }
          return old;
        },
      );
      queryClient.setQueryData<IUseQueryFilesReturn>(
        [
          QUERY_KEY_MOVE_FILES_LIST,
          Number(variables.constructionSiteId),
          variables.folderId,
        ],
        (old) => {
          if (old) {
            return {
              ...old,
              ListFiles: old.ListFiles.map((file) => {
                const index = data.data.findIndex((info) => {
                  return info.ApiId === file.Identifier.ApiId;
                });
                if (index !== -1) {
                  return {
                    ...file,
                    FileInfo: {
                      ...file.FileInfo,
                      ...data.data[index],
                      Description: data.data[index].Description,
                      finished: true,
                    },
                    Status: data.data[index].Status,
                  };
                }
                return file;
              }),
            };
          }
          return old;
        },
      );

      dispatch(UploadActions.updateFileInfo(data.data));
      dispatch(FederatedViewerActions.updateFileViewerInfo(data.data));

      if (!variables.files.find((item) => item.notShowToast === true)) {
        const isStatusEdit = variables.files.every((item) => item.isStatusEdit);
        const onlyOne = variables.files.length === 1 ? '' : 's';

        toastHandler.showSuccess(isStatusEdit
          ? `Status do${onlyOne} arquivo${onlyOne} alterado${onlyOne} com sucesso!`
          : `Descrição do${onlyOne} arquivo${onlyOne} atualizada${onlyOne} com sucesso!`,
        );
      }
    },
    onError: async (error: any) => {
      const errorMsg = error.errors
        ? error.errors[0].Message
        : 'Erro ao realizar atualização dos arquivos';
      toastHandler.showError(errorMsg);
    },
  });

  return {
    updateInfoFiles: mutate,
    isLoading: isPending,
  };
};
